import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost, type ApiError } from './api';
import { API_BASE } from './apiBase';

const CONTENT_BASE = `${API_BASE}/v1/content`;

export type ContentItem = {
  id: number;
  area: string;
  title: string;
  body_html: string | null;
  fields: Record<string, unknown> | null;
  status: string;
  store_no: string | null;
  owner_email: string;
  review_date: string | null;
  published_at: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  attachments: { id: number; filename: string; content_type: string }[];
};

export type SubmissionBody = {
  area: 'recognition' | 'ideas';
  title: string;
  body_html?: string;
  attachment?: { filename: string; content_type: string };
  store_no?: string;
};

type SubmissionResult = {
  item: ContentItem;
  attachment: { attachment_id: number; key: string; upload_url: string } | null;
};

export function usePublishedContent(area: string): UseQueryResult<ContentItem[], Error> {
  return useQuery<ContentItem[], Error>({
    queryKey: ['content', 'published', area],
    queryFn: async () => {
      try {
        const res = await apiGet<{ items: ContentItem[] }>(`${CONTENT_BASE}/published?area=${area}`);
        return res.items;
      } catch (err) {
        if ((err as ApiError).status === 404) return [];
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useSubmitContent() {
  return useMutation({
    mutationFn: async ({ body, file }: { body: SubmissionBody; file?: File }) => {
      const result = await apiPost<SubmissionResult>(`${CONTENT_BASE}/submissions`, body);
      if (file && result.attachment?.upload_url) {
        await fetch(result.attachment.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
      }
      return result;
    },
  });
}
